/**
 * Internal Admin Onboarding UI
 * ============================
 * Hidden page at /admin/onboard. Not linked from public nav. Uses the
 * existing admin Bearer API key (ak_…). Calls POST /api/admin/onboard-customer.
 *
 * Two actions:
 *  • Run Dry-Run   — preview only, no DB writes
 *  • Onboard Now   — confirmation dialog, then live writes
 */
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ShieldCheck, KeyRound, Copy, CheckCircle2, AlertCircle, Rocket, Eye,
  Phone, Mail, Globe, Building2, Loader2, Lock,
  User, FileText, MapPin,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { id: 'real_estate',          label: 'Real Estate' },
  { id: 'drywall',              label: 'Drywall' },
  { id: 'plumbing',             label: 'Plumbing' },
  { id: 'hvac',                 label: 'HVAC' },
  { id: 'bathroom_remodeling',  label: 'Bathroom Remodeling' },
  { id: 'general_construction', label: 'General Construction' },
];

const SLUG_RE   = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;
const EMAIL_RE  = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_RE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const PHONE_RE  = /^\+?[1-9]\d{7,14}$/;
const EIN_RE    = /^\d{9}$/;                 // 9 digits after stripping dashes
const US_ZIP_RE = /^\d{5}(?:-\d{4})?$/;
const STATE_RE  = /^[A-Za-z]{2}$/;

const KEY_STORAGE = 'agent9_admin_api_key';

export default function AdminOnboardCustomerPage() {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(KEY_STORAGE) || '');
  const [unlocked, setUnlocked] = useState(false);
  const [keyInput, setKeyInput] = useState('');

  useEffect(() => {
    if (apiKey) setUnlocked(true);
  }, [apiKey]);

  const saveKey = () => {
    if (!keyInput.trim().startsWith('ak_')) {
      toast.error('API key must start with "ak_"');
      return;
    }
    localStorage.setItem(KEY_STORAGE, keyInput.trim());
    setApiKey(keyInput.trim());
    setUnlocked(true);
  };

  const clearKey = () => {
    localStorage.removeItem(KEY_STORAGE);
    setApiKey('');
    setUnlocked(false);
    setKeyInput('');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-onboard-page">
      <Helmet><title>Admin · Onboard Customer · Agent9</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <ShieldCheck size={18} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Onboard Customer</h1>
            <p className="text-sm text-slate-400">Internal-only · do not share this URL</p>
          </div>
        </div>

        {!unlocked ? (
          <ApiKeyGate keyInput={keyInput} setKeyInput={setKeyInput} onUnlock={saveKey} />
        ) : (
          <OnboardingForm apiKey={apiKey} onClearKey={clearKey} />
        )}
      </div>
    </div>
  );
}

// ── API key gate ──────────────────────────────────────────────────────────
function ApiKeyGate({ keyInput, setKeyInput, onUnlock }) {
  return (
    <Card className="bg-slate-900/60 border-slate-800" data-testid="api-key-gate">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock size={16} /> Admin API key required
        </CardTitle>
        <CardDescription>
          Paste your Agent9 admin Bearer key. It is stored locally in your browser only;
          revoke at any time from the Admin → API Keys console.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label htmlFor="api-key">API key</Label>
          <Input id="api-key" type="password" placeholder="ak_…"
            value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onUnlock(); }}
            data-testid="api-key-input"
            className="bg-slate-950 border-slate-800 mt-1" />
        </div>
        <Button onClick={onUnlock} data-testid="api-key-unlock-btn"
          className="bg-emerald-600 hover:bg-emerald-500">
          <KeyRound size={14} className="mr-1" /> Unlock
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Form ──────────────────────────────────────────────────────────────────
function OnboardingForm({ apiKey, onClearKey }) {
  const [form, setForm] = useState({
    slug: '', domain: '', phone: '', industry: 'drywall',
    email: '', business_name: '', password: '',
    owner_full_name: '', ein: '',
    addr_street1: '', addr_street2: '', addr_city: '',
    addr_state: '', addr_zip: '', addr_country: 'US',
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!SLUG_RE.test(form.slug.toLowerCase()))
      e.slug = 'lowercase letters, digits, dashes (3–64 chars)';
    if (!DOMAIN_RE.test(form.domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase()))
      e.domain = 'expected bare host like evolvedrywall.com';
    if (!PHONE_RE.test(form.phone.replace(/[^\d+]/g, '')))
      e.phone = 'expected E.164 (e.g. +18313287100)';
    if (!INDUSTRIES.find(i => i.id === form.industry))
      e.industry = 'select an industry';
    if (!EMAIL_RE.test(form.email))
      e.email = 'expected a valid email';
    if (!form.owner_full_name.trim() || !form.owner_full_name.includes(' '))
      e.owner_full_name = 'first + last name (required by SignalWire 10DLC)';
    if (!EIN_RE.test(form.ein.replace(/\D/g, '')))
      e.ein = 'expected 9-digit US EIN like 12-3456789';
    if (!form.addr_street1.trim()) e.addr_street1 = 'required';
    if (!form.addr_city.trim())    e.addr_city    = 'required';
    if (form.addr_country.trim().toUpperCase() === 'US') {
      if (!STATE_RE.test(form.addr_state))   e.addr_state = '2-letter state code (e.g. CA)';
      if (!US_ZIP_RE.test(form.addr_zip))    e.addr_zip   = '5-digit ZIP or ZIP+4';
    } else {
      if (!form.addr_state.trim()) e.addr_state = 'required';
      if (!form.addr_zip.trim())   e.addr_zip   = 'required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (dryRun) => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields first');
      return;
    }
    setBusy(true); setResult(null);
    try {
      const payload = {
        slug:     form.slug.toLowerCase().trim(),
        domain:   form.domain.trim(),
        phone:    form.phone.trim(),
        industry: form.industry,
        email:    form.email.trim(),
        owner_full_name: form.owner_full_name.trim(),
        ein:      form.ein.trim(),
        business_address: {
          street1: form.addr_street1.trim(),
          street2: form.addr_street2.trim() || null,
          city:    form.addr_city.trim(),
          state:   form.addr_state.trim(),
          postal_code: form.addr_zip.trim(),
          country: (form.addr_country.trim() || 'US').toUpperCase(),
        },
        dry_run:  dryRun,
      };
      if (form.business_name.trim()) payload.business_name = form.business_name.trim();
      if (form.password.trim())      payload.password = form.password.trim();

      const res = await axios.post(`${API}/admin/onboard-customer`, payload, {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      setResult(res.data);
      toast.success(dryRun ? 'Dry-run complete' : 'Customer onboarded');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message;
      if (status === 401) {
        toast.error('Unauthorized — check your API key');
      } else if (status === 422) {
        toast.error(`Validation error: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
      } else {
        toast.error(`${status || ''} ${typeof detail === 'string' ? detail : 'Request failed'}`);
      }
    }
    setBusy(false);
  };

  const handleLiveSubmit = () => { setConfirmOpen(false); submit(false); };

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">
          API key unlocked
        </Badge>
        <Button variant="ghost" size="sm" onClick={onClearKey}
                data-testid="clear-api-key-btn"
                className="text-slate-400 hover:text-slate-100">
          Forget key
        </Button>
      </div>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader>
          <CardTitle>Customer details</CardTitle>
          <CardDescription>Fill in the fields and run a dry-run first to verify.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field id="slug" label="Slug" placeholder="evolvedrywall"
            icon={<Building2 size={14} />} value={form.slug}
            onChange={(v) => set('slug', v)} error={errors.slug}
            help="URL-safe identifier (lowercase, dashes)." />

          <Field id="domain" label="Domain" placeholder="evolvedrywall.com"
            icon={<Globe size={14} />} value={form.domain}
            onChange={(v) => set('domain', v)} error={errors.domain}
            help="Bare host without protocol or trailing slash." />

          <Field id="phone" label="Phone (E.164)" placeholder="+18313287100"
            icon={<Phone size={14} />} value={form.phone}
            onChange={(v) => set('phone', v)} error={errors.phone}
            help="Including country code; 10-digit US numbers auto-prefix +1." />

          <div>
            <Label htmlFor="industry">Industry</Label>
            <select id="industry" value={form.industry} data-testid="onboard-industry"
              onChange={(e) => set('industry', e.target.value)}
              className="mt-1 w-full rounded-md bg-slate-950 border border-slate-800 px-3 py-2 text-sm">
              {INDUSTRIES.map(i =>
                <option key={i.id} value={i.id}>{i.label}</option>)}
            </select>
            {errors.industry && <p className="text-xs text-rose-400 mt-1">{errors.industry}</p>}
          </div>

          <Field id="email" label="Owner email" placeholder="info@evolvedrywall.com"
            icon={<Mail size={14} />} type="email" value={form.email}
            onChange={(v) => set('email', v)} error={errors.email} />

          {/* ── Business info (required by SignalWire 10DLC campaign registration) ── */}
          <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 space-y-4">
            <div className="flex items-start gap-2 text-xs text-amber-300/90">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <p>
                The fields below are required for SignalWire 10DLC campaign
                registration. Use the legal business address and EIN exactly
                as registered with the IRS.
              </p>
            </div>

            <Field id="owner_full_name" label="Owner full name" placeholder="John Doe"
              icon={<User size={14} />} value={form.owner_full_name}
              onChange={(v) => set('owner_full_name', v)} error={errors.owner_full_name}
              help="First + last name (required for 10DLC)." />

            <Field id="ein" label="EIN (US Tax ID)" placeholder="12-3456789"
              icon={<FileText size={14} />} value={form.ein}
              onChange={(v) => set('ein', v)} error={errors.ein}
              help="9-digit Employer Identification Number." />

            <div>
              <Label className="flex items-center gap-1.5 text-slate-200">
                <MapPin size={14} /> Full business address
              </Label>
              <div className="mt-1 grid grid-cols-1 sm:grid-cols-6 gap-3">
                <div className="sm:col-span-6">
                  <Input placeholder="Street address (line 1)" data-testid="onboard-addr_street1"
                    value={form.addr_street1} onChange={(e) => set('addr_street1', e.target.value)}
                    className={`bg-slate-950 border-slate-800 ${errors.addr_street1 ? 'border-rose-500' : ''}`} />
                  {errors.addr_street1 && <p className="text-xs text-rose-400 mt-1">{errors.addr_street1}</p>}
                </div>
                <div className="sm:col-span-6">
                  <Input placeholder="Suite / unit (optional)" data-testid="onboard-addr_street2"
                    value={form.addr_street2} onChange={(e) => set('addr_street2', e.target.value)}
                    className="bg-slate-950 border-slate-800" />
                </div>
                <div className="sm:col-span-3">
                  <Input placeholder="City" data-testid="onboard-addr_city"
                    value={form.addr_city} onChange={(e) => set('addr_city', e.target.value)}
                    className={`bg-slate-950 border-slate-800 ${errors.addr_city ? 'border-rose-500' : ''}`} />
                  {errors.addr_city && <p className="text-xs text-rose-400 mt-1">{errors.addr_city}</p>}
                </div>
                <div className="sm:col-span-1">
                  <Input placeholder="ST" maxLength={3} data-testid="onboard-addr_state"
                    value={form.addr_state}
                    onChange={(e) => set('addr_state', e.target.value.toUpperCase())}
                    className={`bg-slate-950 border-slate-800 ${errors.addr_state ? 'border-rose-500' : ''}`} />
                  {errors.addr_state && <p className="text-xs text-rose-400 mt-1">{errors.addr_state}</p>}
                </div>
                <div className="sm:col-span-1">
                  <Input placeholder="ZIP" data-testid="onboard-addr_zip"
                    value={form.addr_zip} onChange={(e) => set('addr_zip', e.target.value)}
                    className={`bg-slate-950 border-slate-800 ${errors.addr_zip ? 'border-rose-500' : ''}`} />
                  {errors.addr_zip && <p className="text-xs text-rose-400 mt-1">{errors.addr_zip}</p>}
                </div>
                <div className="sm:col-span-1">
                  <Input placeholder="US" maxLength={3} data-testid="onboard-addr_country"
                    value={form.addr_country}
                    onChange={(e) => set('addr_country', e.target.value.toUpperCase())}
                    className="bg-slate-950 border-slate-800" />
                </div>
              </div>
            </div>
          </div>

          <details className="text-sm text-slate-400">
            <summary className="cursor-pointer hover:text-slate-200">
              Optional: business name & password
            </summary>
            <div className="mt-3 space-y-3 pl-1">
              <Field id="business_name" label="Business name (optional)"
                placeholder="Evolve Drywall LLC" value={form.business_name}
                onChange={(v) => set('business_name', v)} />
              <Field id="password" label="First-login password (optional)"
                placeholder="Defaults to slug" value={form.password}
                onChange={(v) => set('password', v)} type="text"
                help="If blank, default password = slug." />
            </div>
          </details>

          <div className="flex flex-col sm:flex-row gap-3 pt-3">
            <Button variant="outline" onClick={() => submit(true)} disabled={busy}
              data-testid="onboard-dry-run-btn"
              className="border-slate-700 hover:bg-slate-800">
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />}
              Run Dry-Run
            </Button>
            <Button onClick={() => setConfirmOpen(true)} disabled={busy}
              data-testid="onboard-live-btn"
              className="bg-emerald-600 hover:bg-emerald-500">
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Rocket size={14} className="mr-1" />}
              Onboard Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && <ResultPanel result={result} />}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="onboard-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-400" />
              Confirm live onboarding
            </DialogTitle>
            <DialogDescription>
              This will create the agent, tenant, subscription (pending_payment),
              phone-registry row, and customer website for{' '}
              <span className="font-medium text-slate-200">{form.email || '—'}</span>.
              Re-runs are idempotent and safe.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleLiveSubmit} data-testid="onboard-confirm-btn"
              className="bg-emerald-600 hover:bg-emerald-500">
              <Rocket size={14} className="mr-1" /> Onboard now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Field helper ──────────────────────────────────────────────────────────
function Field({ id, label, value, onChange, icon, type = 'text',
                 placeholder, help, error }) {
  return (
    <div>
      <Label htmlFor={id} className="flex items-center gap-1.5 text-slate-200">
        {icon}{label}
      </Label>
      <Input id={id} type={type} value={value} placeholder={placeholder}
        data-testid={`onboard-${id}`}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 bg-slate-950 border-slate-800 ${error ? 'border-rose-500' : ''}`} />
      {error
        ? <p className="text-xs text-rose-400 mt-1" data-testid={`error-${id}`}>{error}</p>
        : help ? <p className="text-xs text-slate-500 mt-1">{help}</p> : null}
    </div>
  );
}

// ── Result panel ──────────────────────────────────────────────────────────
function ResultPanel({ result }) {
  // Auto-redirect to the template picker after a successful live run
  const [secondsLeft, setSecondsLeft] = useState(
    !result.dry_run && result.template_picker_url ? 5 : 0
  );

  useEffect(() => {
    if (!secondsLeft) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  useEffect(() => {
    if (secondsLeft === 0 && !result.dry_run && result.template_picker_url) {
      // Open in new tab so the admin keeps the result panel open
      window.open(result.template_picker_url, '_blank', 'noopener');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  const cancelRedirect = () => setSecondsLeft(0);
  // Treat any countdown > 0 as "redirecting"
  const isRedirecting = secondsLeft > 0;

  return (
    <Card className="bg-slate-900/80 border-emerald-500/30" data-testid="onboard-result">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-400" />
          {result.dry_run ? 'Dry-run preview' : 'Onboarding complete'}
        </CardTitle>
        <CardDescription>
          {result.dry_run
            ? 'Plan validated. No database changes were made.'
            : (result.agent_created
                ? 'New agent + customer website created.'
                : 'Existing customer found — fields refreshed in place.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">

        {isRedirecting && result.template_picker_url && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 flex items-center justify-between gap-3"
               data-testid="redirect-banner">
            <p className="text-sm">
              Redirecting to choose a website template in <span className="font-mono font-semibold">{secondsLeft}</span>s…
            </p>
            <button onClick={cancelRedirect}
                    className="text-xs text-slate-300 hover:text-white underline"
                    data-testid="cancel-redirect-btn">
              Stay on this page
            </button>
          </div>
        )}

        <ResultRow label="Tenant ID"        value={result.tenant_id  || '—'} />
        <ResultRow label="Agent ID"         value={result.agent_id   || '—'} />
        <ResultRow label="Website ID"       value={result.website_id || '—'} />
        <ResultRow label="Industry"         value={result.industry} />
        <ResultRow label="Owner full name"  value={result.owner_full_name} copy />
        <ResultRow label="EIN"              value={result.ein}              copy />
        <ResultRow label="Phone"            value={result.phone}             copy />
        <ResultRow label="Owner email"      value={result.owner_email}        copy />
        <ResultRow label="Assistant name"   value={result.assistant_name} />
        <ResultRow label="Preview URL"      value={result.preview_url}        copy link />
        <ResultRow label="Custom domain"    value={result.custom_domain_url}  copy link />

        {result.business_address && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Business address</p>
            <div className="text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-md p-3">
              <p>{result.business_address.street1}</p>
              {result.business_address.street2 && <p>{result.business_address.street2}</p>}
              <p>
                {result.business_address.city}, {result.business_address.state}{' '}
                {result.business_address.postal_code} · {result.business_address.country}
              </p>
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs uppercase tracking-wide text-slate-500">DNS records</p>
            <CopyButton text={result.dns_instructions} testId="copy-dns" label="copy" />
          </div>
          <pre className="text-xs leading-relaxed bg-slate-950 border border-slate-800 rounded-md p-3 whitespace-pre-wrap font-mono text-slate-300">
{result.dns_instructions}
          </pre>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Next steps</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-slate-200">
            {result.next_steps?.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>

        {result.agent_id && (
          <div className="pt-3">
            <a
              data-testid="choose-website-template-cta"
              href={result.template_picker_url
                || `/onboarding/website-template?agent_id=${result.agent_id}&tenant_id=${result.tenant_id || ''}&slug=${result.slug || ''}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium">
              Choose website template now →
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResultRow({ label, value, copy, link }) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b border-slate-800/60">
      <span className="text-slate-500 text-xs uppercase tracking-wide">{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        {link
          ? <a href={value} target="_blank" rel="noreferrer"
                className="text-emerald-400 hover:text-emerald-300 truncate max-w-[18rem]"
                data-testid={`result-${label.toLowerCase().replace(/\s+/g,'-')}`}>
              {value}
            </a>
          : <span className="text-slate-200 truncate max-w-[18rem] font-mono text-xs"
                  data-testid={`result-${label.toLowerCase().replace(/\s+/g,'-')}`}>
              {value}
            </span>}
        {copy && value && value !== '—'
          && <CopyButton text={value} testId={`copy-${label.toLowerCase().replace(/\s+/g,'-')}`} />}
      </div>
    </div>
  );
}

function CopyButton({ text, testId, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); }
        catch { /* ignore */ }
        setCopied(true); setTimeout(() => setCopied(false), 1500);
      }}
      data-testid={testId}
      className="text-xs text-slate-400 hover:text-emerald-400 inline-flex items-center gap-1">
      {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
      {label || (copied ? 'copied' : '')}
    </button>
  );
}
